import type {
  TemplatePreparation,
  TemplateReview,
} from '@app/application/workflows/types'
import type { SyncStatusValue } from '@app/insights/constants'

export const segmentVisibilities = {
  PRIVATE_VISIBLE: 'private_visible',
  ADMIN_VISIBLE: 'admin_visible',
  FIRM_VISIBLE: 'firm_visible',
} as const

export type SegmentVisibility =
  typeof segmentVisibilities[keyof typeof segmentVisibilities]

export type SegmentVisibilityDataItem = {
  path: string
  name: string
  desc: string
}

export type SegmentVisibilityData = Record<
  SegmentVisibility,
  SegmentVisibilityDataItem
>

export type SegmentPermission = 'update' | 'update_visibility' | 'destroy'

// Who published the query: the firm's own users, Silverfin, or a third-party
// partner. Derived server-side from the query's partner organisation.
export type SegmentPublisher = 'firm' | 'silverfin' | 'partner'

export type Segment = {
  id: number
  name: string
  name_en?: string | null
  recommended: boolean
  publisher: SegmentPublisher
  class?: string
  visibility: SegmentVisibility
  permissions: Partial<Record<SegmentPermission, boolean>>
  partner?: string
  insight_triggers_count: number
  partner_segment_id?: number | null
  last_request_ref?: string | null
  last_segment_run_finished?: boolean
  last_visited_at?: string | null
  period_date?: string | null
  period_lock_type?: string | null
  period_type?: string | null
  description?: string | null
  localised_names?: Record<string, string | null>
  localised_descriptions?: Record<string, string | null>
  incompatibility_reasons?: string[]
  created_at: string
}

export type SegmentDraft = Omit<
  Segment,
  | 'id'
  | 'created_at'
  | 'last_visited_at'
  | 'name_en'
  | 'description'
  | 'publisher'
> & { id: number | null }

// SFetch rejects with the parsed JSON body attached as `body`. The segment and
// pinned-segment endpoints hand-render `{ errorMessage }` on failure (see
// SegmentsController / PinnedSegmentsController), which is not the canonical
// ResponseError shape, so callers narrow to this when surfacing the message.
export type SegmentErrorResponse = { body: { errorMessage: string } }

export type EditSegmentModalType = 'save_as' | 'save' | 'edit'

export type WorkflowPreparationReviewCellData = {
  no_data?: boolean
  label: string
  prepared_count?: number
  reviewed_count?: number
  total_count: number
  users: Array<{ name: string }>
}

export type PreparationAndReviewCellData = {
  no_data: boolean
  reviews?: TemplateReview[]
  preparation?: TemplatePreparation
} & Partial<WorkflowPreparationReviewCellData>

export type SyncStatusCellData = {
  status: SyncStatusValue
  timestamp?: string
  error_message?: string
}

export const periodLockStatuses = {
  NOT_LOCKED: 'not_locked',
  LOCKED: 'locked',
  PERMANENTLY_LOCKED: 'permanently_locked',
} as const

export type PeriodLockStatus =
  typeof periodLockStatuses[keyof typeof periodLockStatuses]

export type PeriodLockedCellData = {
  status: PeriodLockStatus
  locked_at: string | null
  locked_by_name: string | null
}

export const periodLockStatusLabelKeys: Record<PeriodLockStatus, string> = {
  [periodLockStatuses.NOT_LOCKED]:
    'companies_overview.filters.period_locked.not_locked',
  [periodLockStatuses.LOCKED]:
    'companies_overview.filters.period_locked.locked_option',
  [periodLockStatuses.PERMANENTLY_LOCKED]:
    'companies_overview.filters.period_locked.locked_permanently_option',
}

// Sort order so the two locked states group together, with Not locked first.
export const periodLockStatusOrder: Record<PeriodLockStatus, number> = {
  [periodLockStatuses.NOT_LOCKED]: 0,
  [periodLockStatuses.LOCKED]: 1,
  [periodLockStatuses.PERMANENTLY_LOCKED]: 2,
}

export type TemplateStarringStatus =
  | 'starred'
  | 'partially_starred'
  | 'not_starred'

export type TemplateStarringAccountItem = {
  id: number
  label: string
  href: string
  starred_by?: string | null
  starred_at?: string | null
}

// Counts and lookup keys only — the account lists are fetched on demand when
// the details drawer opens (see useAccountsListDrawer).
export type TemplateStarringAccountsCellData = {
  kind: 'accounts'
  status: TemplateStarringStatus
  starred_count: number
  total_count: number
  ledger_id: number
  account_detail_template_id: number
}

export type TemplateStarringReconciliationCellData = {
  kind: 'reconciliation'
  starred: boolean
}

// Completion state of a file's to-dos, used to pick the cell icon.
export type TodoStatus = 'not_done' | 'partially_done' | 'done'

// Criteria the To-do completion filter can be set to. Insertion order is the
// order the options are offered in the filter dropdown.
export const todoFilterStatuses = {
  SHOW_ALL: 'show_all',
  OPEN: 'open',
  DONE: 'done',
} as const

export type TodoFilterStatus =
  typeof todoFilterStatuses[keyof typeof todoFilterStatuses]

export type TodosCellData = {
  status: TodoStatus | null
  completed: number
  total: number
}

export type CompaniesListCell = {
  href: string
  uuid: string
  formatted_value:
    | string
    | PreparationAndReviewCellData
    | SyncStatusCellData
    | PeriodLockedCellData
    | TemplateStarringAccountsCellData
    | TemplateStarringReconciliationCellData
    | TodosCellData
  filter_error?: string
}

export type InsightsFilter = {
  uuid: string
  type: string
  settings: Record<string, string | number>
}

export type InsightsColumn = {
  filter: InsightsFilter
  name: string
}

export type InsightsColumnAction = {
  titleKey: typeof insightColumnActionTitleKeys[keyof typeof insightColumnActionTitleKeys]
  click: (param?: number) => void
}

export const insightColumnActionTitleKeys = {
  MOVE_LEFT: 'move_left',
  MOVE_RIGHT: 'move_right',
  SORT_ASC: 'sort_ascending',
  SORT_DESC: 'sort_descending',
  EDIT: 'edit',
  REMOVE: 'remove',
} as const

export type UpdateSortPayload = {
  sortColumn: string
  sortType: string
}
export type UpdateFilterPositionPayload = {
  uuid: string
  position: number
}

export type Job = {
  insight_action_run_id: number
  running: boolean
  finished: boolean
  processed_count: number
  total_count: number
  action_name?: string
  payload: { [key: string]: unknown }
  user_id: number
  username: string
}

export type Company = {
  id: string
  name: string
}

export type FirmWorkflow = {
  id: string
  name: string
}

export const periodSelectionTypes = {
  FINANCIAL_YEAR: 'financial_year',
  ACCOUNTING_PERIOD: 'accounting_period',
  ACTIVE_PERIOD: 'active_period',
  COMPLETED_PERIOD: 'completed_period',
} as const

export type PeriodSelectionType =
  typeof periodSelectionTypes[keyof typeof periodSelectionTypes]

export type PeriodSelectionOption = {
  key: PeriodSelectionType
  titleKey: string
  subtitleKey?: string
  icon: string
  illustration: string
  tooltipKey: string
}

export interface PeriodSelectionData {
  periodSelection: string | null
  ledgerSelectDate: string | null
  ledgerSelectLockType: LockType | undefined
  saveCallback: (data: {
    periodSelection: string | null
    ledgerSelectDate: string | null
    ledgerSelectLockType: LockType | undefined
  }) => void
}

export const dateTypes = {
  TODAY: 'today',
  FIXED_DATE: 'fixed_date',
} as const

export type DateType = typeof dateTypes[keyof typeof dateTypes]

export const lockTypes = {
  LOCKED: 'locked',
  PERMANENTLY_LOCKED: 'permanently_locked',
} as const

export type LockType = typeof lockTypes[keyof typeof lockTypes]

export type FilterDefinition = {
  type: string
  nameKey: string
  deployDate?: string
  ledgerRequired?: boolean
  hasModalHelp?: boolean
  shouldShow?: (context: Record<string, unknown>) => boolean
  disabledOnFirmLevel?: () => boolean
  // Extra terms the Add-filter search should match on, beyond the filter's
  // title. Lets a filter be found by a spelling the title can't match — e.g.
  // "to do" (with a space) won't line up with the hyphen in "To-do completion".
  searchKeywords?: string
  searchableAttributes?: () => {
    settings: Record<string, string>
    label: string
  }[]
}

export type SearchableFilterDefinition = FilterDefinition & {
  name: string
  description: string
  attributeSettings?: Record<string, string>
  attributeLabel?: string
}

export interface FilterGroup {
  nameKey: string
  filterTypes: string[]
}

export interface ReviewState {
  hasReviews: boolean
  hasAnyUpToDateReview: boolean
  prefix: string
}
